import { useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAtom } from "jotai";
import { useSpotUser } from "@/utils/spotUser";
import {
  closeBlock,
  neighbourAfterClose,
  openBlocksByGroupAtom,
  sameKindAfterDelete,
} from "@/store/open-blocks";
import {
  blockHref,
  toolAt,
  TYPE_CONFIG,
  type ToolType,
} from "@/components/Layouts/blockTypes";
import { useOpenBlockRecords } from "@/components/Layouts/useOpenTabsParam";

// Query params that act once on arrival (open the runner, preview a run,
// start a version, add tools, open a table row): carried to another section
// or kept in an open tool's link, they would act again on every visit.
export const ONE_SHOT_PARAMS = [
  "openRunner",
  "runId",
  "preview_run",
  "createVersion",
  "openTabs",
  "row",
];

// The tool the URL is on, if any.
export const useCurrentPage = (): {
  type: ToolType | null;
  id: string | null;
} => {
  const pathname = usePathname();
  return toolAt(pathname ?? "") ?? { type: null, id: null };
};

// Removes a tool from the open tools. When it is the one on screen, the page
// moves to its neighbour in the list (skipping deleted tools), or home, and the
// tool is removed only once the page has left it: a move that waits for a save
// and then is refused (a failed agent save) keeps it open. A deleted tool
// moves to the nearest open tool of its kind instead, else its list.
export const useCloseBlock = () => {
  const [byGroup, setByGroup] = useAtom(openBlocksByGroupAtom);
  const { spotGroup } = useSpotUser();
  const router = useRouter();
  const { gone } = useOpenBlockRecords(byGroup[spotGroup.id] ?? []);

  return useCallback(
    (
      id: string,
      isOnScreen: boolean,
      navigate: (href: string, onLeave: () => void) => void = (
        href,
        onLeave
      ) => {
        router.push(href);
        onLeave();
      },
      { deleted = false }: { deleted?: boolean } = {}
    ) => {
      const remove = () =>
        setByGroup((prev) => ({
          ...prev,
          [spotGroup.id]: closeBlock(prev[spotGroup.id] ?? [], id),
        }));
      if (!isOnScreen) return remove();
      const listed = (byGroup[spotGroup.id] ?? []).filter(
        (block) => block.id === id || !gone.has(block.id)
      );
      const closing = listed.find((block) => block.id === id);
      const next = deleted
        ? sameKindAfterDelete(listed, id)
        : neighbourAfterClose(listed, id);
      const fallback =
        deleted && closing ? TYPE_CONFIG[closing.type].listHref : "/";
      navigate(next ? blockHref(next) : fallback, remove);
    },
    [byGroup, gone, setByGroup, spotGroup.id, router]
  );
};

// A deleted tool leaves the open tools, and the page if it was on it.
export const useCloseTabAndRedirect = () => {
  const close = useCloseBlock();
  const { id: currentId } = useCurrentPage();
  return useCallback(
    (id: string) => close(id, id === currentId, undefined, { deleted: true }),
    [close, currentId]
  );
};
